import React, { useState, useEffect, useCallback, useId, useMemo } from 'react';
import { User, Affiliate } from '../types';
import { adminService } from '../services/adminService';
import { authService } from '../services/authService';
import { MessageAudit } from './admin/MessageAudit';
import { ConsentManager } from './admin/ConsentManager';
import { AuditLogViewer } from './admin/AuditLogViewer';
import { RoleManager } from './admin/RoleManager';
import { LoginApprovalManager } from './admin/LoginApprovalManager';
import { DeviceTrustManager } from './admin/DeviceTrustManager';
import { RemoteSupportManager } from './admin/RemoteSupportManager';
import { CalendarEditorManager } from './admin/CalendarEditorManager';
import { BlockedExtensionsManager } from './admin/BlockedExtensionsManager';
import { UploadPolicyManager } from './admin/UploadPolicyManager';
import { ClientVersionList } from './admin/ClientVersionList';
import { InstallDeployManager } from './admin/InstallDeployManager';
import { PushMetricsPanel } from './admin/PushMetricsPanel';
import { PublicChannelsManager } from './admin/PublicChannelsManager';
import { NoticeSender } from './admin/NoticeSender';
import { AdminOverview } from './admin/AdminOverview';
import { AdminPage, AdminNavItem, visibleNav, findNavItem, ROLE_LABELS } from './admin/adminNav';
import { useCloseWatcher } from '../hooks/useCloseWatcher';
import { useIsMobile } from '../hooks/useIsMobile';
import { X, Shield, ChevronLeft, ChevronRight } from 'lucide-react';

interface AdminConsoleProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: User;
}

/** 기기 승인 대기 수. null = 아직 모름, 'error' = 불러오지 못함 */
type PendingCount = number | null | 'error';

/**
 * 관리자 콘솔 셸(2026-11 재설계 — 가로 탭 14개 → 묶음 사이드 메뉴 + '한눈에 보기').
 *
 * - 메뉴 정의(이름·묶음·설명·노출 역할)는 `admin/adminNav.ts` 하나가 소유한다. 이 파일은 배치와 패널 스위치뿐이다.
 * - 데스크톱(md 이상): 왼쪽 메뉴 + 오른쪽 화면. 화면 머리(제목·설명)는 **스크롤 영역 밖**에 고정한다 —
 *   패널 컨테이너가 `flex-1 min-h-0 overflow-y-auto flex flex-col`이어야 adminLayout.ts의 높이 계약이 산다.
 * - 좁은 화면(md 미만 — 휴대폰 웹·PWA·좁힌 PC앱): '메뉴(한눈에 보기 + 메뉴 목록) → 화면' 2단계.
 *   두 배치를 CSS로 동시에 그리지 않고 폭으로 골라 렌더한다 — 같이 마운트하면 한눈에 보기 요청이 2벌 나간다.
 *   여기서 useIsMobile은 "폭이 좁다"는 뜻 그대로다(PC앱도 좁으면 2단계 — 의도).
 * - 콘솔은 닫혀도 언마운트되지 않고 null만 그리므로, 열 때마다 첫 화면(한눈에 보기)으로 되돌린다(사용자 결정).
 */
export const AdminConsole: React.FC<AdminConsoleProps> = ({ isOpen, onClose, currentUser }) => {
  const titleId = useId();
  const pageTitleId = useId();
  const isNarrow = useIsMobile();

  const nav = useMemo(() => visibleNav(currentUser.role), [currentUser.role]);
  const visibleKeys = useMemo(
    () => new Set<AdminPage>(nav.flatMap(g => g.items.map(i => i.key))),
    [nav]
  );
  const canSeePage = useCallback((page: AdminPage) => visibleKeys.has(page), [visibleKeys]);

  const [activePage, setActivePage] = useState<AdminPage>('overview');
  /** 좁은 화면의 단계. md 이상에서는 무시된다 */
  const [narrowView, setNarrowView] = useState<'menu' | 'page'>('menu');

  useEffect(() => {
    if (isOpen) {
      setActivePage('overview');
      setNarrowView('menu');
    }
  }, [isOpen]);

  // 역할이 바뀌어(재로그인) 보던 화면이 사라지면 첫 화면으로 — 메뉴 없이 패널만 남는 상태 방지
  useEffect(() => {
    if (!visibleKeys.has(activePage)) setActivePage('overview');
  }, [visibleKeys, activePage]);

  const selectPage = useCallback((page: AdminPage) => {
    setActivePage(page);
    // 한눈에 보기는 좁은 화면에서 메뉴 단계에 이미 있다
    setNarrowView(page === 'overview' ? 'menu' : 'page');
  }, []);

  // 안드로이드 뒤로가기(모바일 웹만): 화면 → 메뉴 → 콘솔 닫힘. 나중에 만든 watcher가 먼저 닫힌다
  useCloseWatcher(isOpen, onClose);
  useCloseWatcher(isOpen && isNarrow && narrowView === 'page', () => setNarrowView('menu'));

  // '기기 승인' 배지·한눈에 보기 — 대기 건수. 새 요청·처리는 소켓(device:pending-changed)으로 온다
  const [pendingDevices, setPendingDevices] = useState<PendingCount>(null);
  const canFetchPending = visibleKeys.has('devices') && currentUser.role === 'super_admin';
  const loadPending = useCallback(() => {
    if (!canFetchPending) return;
    adminService.getDevices({ status: 'pending' })
      .then((r) => setPendingDevices(r.counts.pending))
      .catch(() => setPendingDevices((prev) => (typeof prev === 'number' ? prev : 'error')));
  }, [canFetchPending]);
  useEffect(() => {
    if (!isOpen || !canFetchPending) return undefined;
    loadPending();
    window.addEventListener('device:pending-changed', loadPending);
    return () => window.removeEventListener('device:pending-changed', loadPending);
  }, [isOpen, canFetchPending, loadPending]);
  // role이 없던 시절의 저장 세션은 메뉴는 보이되 대기 수를 묻지 않는다 — 한눈에 보기가 영원히 로딩으로 남지 않게 0
  const pendingForView: PendingCount = canFetchPending ? pendingDevices : 0;
  const pendingBadge = typeof pendingDevices === 'number' && canFetchPending ? pendingDevices : 0;

  // 머리 부제의 계열사 라벨(raw ID 대신)
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  useEffect(() => {
    if (!isOpen || affiliates.length > 0) return;
    authService.getAffiliates().then(setAffiliates).catch(() => { /* 라벨이 없으면 ID를 쓴다 */ });
  }, [isOpen, affiliates.length]);

  /** 메뉴 안 위·아래 화살표·Home/End 이동(포커스만 옮기고, 고르는 것은 Enter/클릭) */
  const onNavKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const items: HTMLButtonElement[] = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[data-admin-nav]'));
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    let next: number;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else if (e.key === 'ArrowDown') next = idx < 0 ? 0 : (idx + 1) % items.length;
    else next = idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length;
    e.preventDefault();
    items[next].focus();
  }, []);

  if (!isOpen) return null;

  const activeItem = findNavItem(activePage);
  const roleLabel = currentUser.role ? ROLE_LABELS[currentUser.role] ?? '관리자' : '관리자';
  const affiliateLabel = currentUser.affiliateId
    ? affiliates.find(a => a.id === currentUser.affiliateId)?.label ?? currentUser.affiliateId
    : '';
  const scopeLabel = currentUser.role === 'super_admin' ? '전 계열사' : affiliateLabel;
  const showPageOnNarrow = isNarrow && narrowView === 'page';

  const badgeOf = (key: AdminPage) => (key === 'devices' ? pendingBadge : 0);

  const renderPanel = () => {
    switch (activePage) {
      case 'overview':
        return (
          <AdminOverview
            canSeePage={canSeePage}
            pendingDevices={pendingForView}
            onNavigate={selectPage}
            onRefreshPending={loadPending}
          />
        );
      case 'channels': return <PublicChannelsManager />;
      case 'messenger': return <NoticeSender />;
      case 'loginApprovals': return <LoginApprovalManager currentUser={currentUser} />;
      case 'devices': return <DeviceTrustManager />;
      case 'roles': return <RoleManager currentUser={currentUser} />;
      case 'calendarEditors': return <CalendarEditorManager currentUser={currentUser} />;
      case 'remoteSupport': return <RemoteSupportManager currentUser={currentUser} />;
      case 'audit': return <MessageAudit />;
      case 'auditLogs': return <AuditLogViewer />;
      case 'consent': return <ConsentManager />;
      case 'uploadPolicy': return <UploadPolicyManager />;
      case 'fileExtensions': return <BlockedExtensionsManager />;
      case 'clients': return <ClientVersionList />;
      case 'deploy': return <InstallDeployManager currentUser={currentUser} />;
      case 'push': return <PushMetricsPanel />;
      default: return null;
    }
  };

  /** 화면 = 고정 머리 + 스크롤 패널. key로 화면이 바뀔 때 스크롤을 맨 위로 되돌린다 */
  const pageView = (
    <section aria-labelledby={pageTitleId} className="flex-1 min-w-0 min-h-0 flex flex-col">
      <div className="shrink-0 px-4 md:px-6 pt-3 md:pt-5 pb-3 md:pb-4 border-b border-slate-100">
        {/* 좁은 화면은 콘솔 머리가 이미 화면 이름을 보여 주므로 화면 판독기용으로만 남긴다 */}
        <h2 id={pageTitleId} className="sr-only md:not-sr-only text-lg font-bold text-slate-900">
          {activeItem.label}
        </h2>
        <p className="md:mt-0.5 text-xs md:text-sm text-slate-500">{activeItem.description}</p>
      </div>
      {/* flex-col: ADMIN_TAB_FILL 루트가 패널 높이를 받는다. min-h-0이 없으면 패널이 내용만큼 커져 프레임을 밀어낸다 */}
      <div key={activePage} className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 flex flex-col">
        {renderPanel()}
      </div>
    </section>
  );

  return (
    // PC앱 제목 표시줄 아래에서 시작 — 좁은 창에서 전체를 덮으면 창을 옮기거나 닫을 수 없다(TitleBar의 --titlebar-h 주석)
    <div className="fixed inset-x-0 bottom-0 top-[var(--titlebar-h,0px)] bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 md:p-6">
      {/* 모바일: 풀스크린, md 이상: 창 높이를 그대로 쓰는 중앙 모달 (HelpPage 패턴).
          높이를 내용에 맡기지 않는다 — 화면마다 콘솔 크기가 바뀌지 않고, 화면 안 목록이 창 높이를 따라 늘고 준다(admin/adminLayout.ts) */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-none safe-area-top safe-area-bottom md:rounded-2xl shadow-2xl w-full h-full md:max-w-6xl flex flex-col overflow-hidden animate-modal-enter"
      >
        {/* Header */}
        <div className="shrink-0 flex items-center gap-3 px-4 md:px-6 py-3 md:py-4 border-b border-slate-200">
          {showPageOnNarrow ? (
            <button
              type="button"
              onClick={() => setNarrowView('menu')}
              aria-label="관리자 메뉴로 돌아가기"
              className="p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <ChevronLeft size={22} aria-hidden="true" />
            </button>
          ) : (
            <div className="h-9 w-9 shrink-0 bg-brand-600 rounded-lg flex items-center justify-center">
              <Shield size={18} className="text-white" aria-hidden="true" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 id={titleId} className="text-lg font-bold text-slate-800 truncate">
              {showPageOnNarrow ? activeItem.label : '관리자 콘솔'}
            </h1>
            {!showPageOnNarrow && (
              <p className="text-xs text-slate-500 truncate">
                {[roleLabel, scopeLabel].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="관리자 콘솔 닫기"
            className="p-2 -mr-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {isNarrow ? (
          showPageOnNarrow ? (
            <div className="flex-1 min-h-0 flex">{pageView}</div>
          ) : (
            /* 좁은 화면 1단계: 한눈에 보기 + 묶음별 메뉴 목록 */
            <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
              <div className="px-4 py-4 space-y-6">
                <AdminOverview
                  canSeePage={canSeePage}
                  pendingDevices={pendingForView}
                  onNavigate={selectPage}
                  onRefreshPending={loadPending}
                />
                <nav aria-label="관리자 콘솔 메뉴" onKeyDown={onNavKeyDown} className="space-y-5">
                  {nav.filter(g => g.label).map(g => (
                    <section key={g.label} aria-label={g.label ?? undefined}>
                      <h3 className="mb-2 text-sm font-semibold text-slate-600">{g.label}</h3>
                      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                        {g.items.map(item => (
                          <NarrowMenuRow key={item.key} item={item} badge={badgeOf(item.key)} onSelect={selectPage} />
                        ))}
                      </div>
                    </section>
                  ))}
                </nav>
              </div>
            </div>
          )
        ) : (
          <div className="flex-1 min-h-0 flex">
            {/* 왼쪽 메뉴 — HelpPage 사이드바와 같은 틀 */}
            <aside className="flex flex-col w-52 lg:w-60 shrink-0 border-r border-slate-200 bg-slate-50/60">
              <nav aria-label="관리자 콘솔 메뉴" onKeyDown={onNavKeyDown} className="flex-1 min-h-0 overflow-y-auto p-3">
                {nav.map((g, gi) => (
                  <div key={g.label ?? 'top'} className={gi > 0 ? 'mt-4' : ''}>
                    {g.label && (
                      <p className="px-3 pb-1 text-xs font-semibold text-slate-500">{g.label}</p>
                    )}
                    <ul className="space-y-0.5">
                      {g.items.map(item => (
                        <li key={item.key}>
                          <SideNavButton
                            item={item}
                            active={item.key === activePage}
                            badge={badgeOf(item.key)}
                            onSelect={selectPage}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>
            </aside>
            {pageView}
          </div>
        )}
      </div>
    </div>
  );
};

const CountBadge: React.FC<{ count: number; label: string }> = ({ count, label }) => (
  <span
    className="min-w-[20px] h-5 px-1.5 rounded-full bg-brand-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0"
    aria-label={label}
  >
    {count > 99 ? '99+' : count}
  </span>
);

/** 데스크톱 왼쪽 메뉴 항목. 활성 틴트는 brand-100(brand-50은 흰 바탕에서 안 보인다 — 디자인 토큰 규칙) */
const SideNavButton: React.FC<{
  item: AdminNavItem;
  active: boolean;
  badge: number;
  onSelect: (page: AdminPage) => void;
}> = ({ item, active, badge, onSelect }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      data-admin-nav
      aria-current={active ? 'page' : undefined}
      onClick={() => onSelect(item.key)}
      className={`w-full flex items-center gap-2.5 px-3 py-2 min-h-[40px] text-sm rounded-lg text-left transition-colors ${
        active
          ? 'bg-brand-100 text-brand-700 font-semibold'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
    >
      <Icon size={16} className={`shrink-0 ${active ? 'text-brand-600' : 'text-slate-400'}`} aria-hidden="true" />
      <span className="flex-1 min-w-0 truncate">{item.label}</span>
      {badge > 0 && <CountBadge count={badge} label={`승인 대기 ${badge}건`} />}
    </button>
  );
};

/** 좁은 화면 메뉴 행 — MobileSettingsScreen 메뉴 행과 같은 모양 */
const NarrowMenuRow: React.FC<{
  item: AdminNavItem;
  badge: number;
  onSelect: (page: AdminPage) => void;
}> = ({ item, badge, onSelect }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      data-admin-nav
      onClick={() => onSelect(item.key)}
      className="w-full flex items-center gap-3.5 px-4 py-3 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
    >
      <span className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
        <Icon size={18} aria-hidden="true" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] text-slate-800">{item.label}</span>
        <span className="block text-xs text-slate-500 truncate">{item.description}</span>
      </span>
      {badge > 0 && <CountBadge count={badge} label={`승인 대기 ${badge}건`} />}
      <ChevronRight size={18} className="text-slate-300 shrink-0" aria-hidden="true" />
    </button>
  );
};
